import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SharedModule } from '@shared/shared.module';
import { RequestResetPasswordComponent } from '../../pages/request-reset-password/request-reset-password.component';
import { SignInComponent } from '../../pages/sign-in/sign-in.component';
import { SignUpComponent } from '../../pages/sign-up/sign-up.component';
import { WelcomeComponent } from '../../pages/welcome/welcome.component';
import { Router } from '@angular/router';
import { LoginService } from '../../services/login.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { NotificationService } from 'src/app/account/services/notification.service';
import { MessageService } from 'src/app/shared/services/message.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import { zip } from 'rxjs';
import { IUserAuthenticateRequest } from '../../interfaces/login.interfaces';
import { MagicLinkRequestComponent } from '../../pages/magic-link-request/magic-link-request.component';

@Component({
  selector: 'app-account-dialog',
  templateUrl: './account-dialog.component.html',
  styleUrls: ['./account-dialog.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    SignInComponent,
    SignUpComponent,
    WelcomeComponent,
    RequestResetPasswordComponent,
    MagicLinkRequestComponent,
  ],
})
export class AccountDialogComponent implements OnInit {
  @Input() nextPageRoute: string = 'account';
  @Input() idEvent: number | null = null;
  @Input() loginPage = true;
  @Input() showWelcome = true; // New property to control welcome screen
  // New optional inputs for magic login flow
  @Input() magicCode?: string;
  @Input() magicRedirect?: string;

  // Page states: 'welcome', 'login', 'register', 'reset', 'magic', 'magicRequest'
  currentPage: 'welcome' | 'login' | 'register' | 'reset' | 'magic' | 'magicRequest' = 'welcome';
  resetEmailPrefill: string = '';

  public activeModal = inject(NgbActiveModal);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private messageService = inject(MessageService);
  private languageService = inject(LanguageService);

  ngOnInit(): void {
    // If showWelcome is false, go directly to login page
    if (!this.showWelcome) {
      this.currentPage = this.loginPage ? 'login' : 'register';
    }
  }

  changePage() {
    // Toggle between login and register (legacy support)
    if (this.currentPage === 'login') {
      this.currentPage = 'register';
    } else if (this.currentPage === 'register') {
      this.currentPage = 'login';
    }
    this.loginPage = this.currentPage === 'login';
  }

  goToLogin() {
    this.currentPage = 'login';
    this.loginPage = true;
  }

  goToRegister() {
    this.currentPage = 'register';
    this.loginPage = false;
  }

  goToWelcome() {
    this.currentPage = 'welcome';
  }

  handleChangePage(evt?: { to?: 'login' | 'register' | 'reset' | 'magicRequest'; email?: string }) {
    if (evt?.to === 'login') {
      this.goToLogin();
      return;
    }
    if (evt?.to === 'reset') {
      this.resetEmailPrefill = evt?.email ?? '';
      this.currentPage = 'reset';
      return;
    }
    if (evt?.to === 'magicRequest') {
      this.resetEmailPrefill = evt?.email ?? this.resetEmailPrefill;
      this.currentPage = 'magicRequest';
      return;
    }
    this.changePage();
  }

  // Triggered by the route-opener to execute magic login inside the dialog
  startMagicLogin(code?: string, redirectRaw?: string) {
    const magicCode = code ?? this.magicCode ?? '';
    const redirectPath = redirectRaw ?? this.magicRedirect ?? '';

    if (!magicCode) {
      // No code -> go home
      const lang = this.languageService.userSelectedLanguage.code;
      this.activeModal.dismiss();
      this.router.navigateByUrl(`/${lang}/home`, { replaceUrl: true });
      return;
    }

    this.currentPage = 'magic';

    this.loginService
      .magicLoginWithCode(magicCode)
      .subscribe((dataR: IUserAuthenticateRequest) => {
        if (dataR?.authResult === 1) {
          this.authService.authResult = dataR.authResult;
          this.authService.userLogged = dataR.user;

          const lang = this.languageService.userSelectedLanguage.code;
          const decodedRedirect = redirectPath ? decodeURIComponent(redirectPath) : '';
          const go = decodedRedirect
            ? `/${lang}/${decodedRedirect.startsWith('/') ? decodedRedirect.substring(1) : decodedRedirect}`
            : this.authService.userLogged?.idProfile === 1
            ? `/${lang}/admin`
            : `/${lang}/account`;

          zip(
            this.notificationService.getUnreadNotificationsCount(),
            this.messageService.getCountUnreadMessages()
          ).subscribe(([count, total]) => {
            this.notificationService.unreadNotificationsCount = count;
            this.messageService.unreadMessagesCount = total.unreadMessages;
            this.messageService.startTimer();
            this.activeModal.dismiss();
            this.router.navigateByUrl(go, { replaceUrl: true });
          });
        } else {
          const lang = this.languageService.userSelectedLanguage.code;
          this.activeModal.dismiss();
          this.router.navigateByUrl(`/${lang}/home`, { replaceUrl: true });
        }
      });
  }
}
